import React, { useEffect, useState, useRef } from 'react';
import { adminListMedia, adminUploadMedia, adminDeleteMedia } from '../../api/adminApi';

interface MediaItem {
  name: string;
  url: string;
  contentType: string;
  size: number;
}

const AdminMedia: React.FC = () => {
  const [items, setItems] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);
  const [deletingName, setDeletingName] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadMedia = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await adminListMedia();
      setItems(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMedia();
  }, []);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError('');
    setSuccess('');
    try {
      const result = await adminUploadMedia(file);
      setSuccess(`Hochgeladen: ${result.blobName}`);
      await loadMedia();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (name: string) => {
    setDeletingName(name);
    setError('');
    setSuccess('');
    try {
      await adminDeleteMedia(name);
      setSuccess(`Gelöscht: ${name}`);
      await loadMedia();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setDeletingName(null);
      setConfirmDelete(null);
    }
  };

  const copyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    setCopiedUrl(url);
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Media Manager</h1>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,application/pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
            onChange={handleUpload}
            className="hidden"
            id="media-upload"
          />
          <label
            htmlFor="media-upload"
            className={`bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition cursor-pointer ${
              uploading ? 'opacity-50 pointer-events-none' : ''
            }`}
          >
            {uploading ? 'Hochladen...' : '+ Datei hochladen'}
          </label>
        </div>
      </div>

      {error && <div className="bg-red-50 text-red-700 p-3 rounded-lg mb-4 text-sm">{error}</div>}
      {success && <div className="bg-green-50 text-green-700 p-3 rounded-lg mb-4 text-sm">{success}</div>}

      {loading ? (
        <p className="text-gray-500 text-sm">Laden...</p>
      ) : items.length === 0 ? (
        <p className="text-gray-500 text-sm">Noch keine Dateien hochgeladen.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((item) => (
            <div key={item.name} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden group">
              {item.contentType.startsWith('image/') ? (
                <div className="aspect-square bg-gray-100 flex items-center justify-center">
                  <img
                    src={item.url}
                    alt={item.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              ) : (
                <div className="aspect-square bg-gray-100 flex items-center justify-center">
                  <span className="text-4xl text-gray-400">
                    {item.contentType === 'application/pdf' ? 'PDF'
                      : (item.contentType === 'application/msword' || item.contentType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') ? 'DOC'
                      : item.contentType === 'text/plain' ? 'TXT'
                      : 'FILE'}
                  </span>
                </div>
              )}
              <div className="p-3">
                <p className="text-xs text-gray-700 truncate font-medium" title={item.name}>
                  {item.name}
                </p>
                <p className="text-xs text-gray-400 mt-1">{formatSize(item.size)}</p>
                <div className="mt-2 flex items-center gap-3">
                  <button
                    onClick={() => copyUrl(item.url)}
                    className="text-xs text-primary-500 hover:text-primary-700 transition"
                  >
                    {copiedUrl === item.url ? 'Kopiert!' : 'URL kopieren'}
                  </button>
                  {confirmDelete === item.name ? (
                    <span className="flex items-center gap-1">
                      <button
                        onClick={() => handleDelete(item.name)}
                        disabled={deletingName === item.name}
                        className="text-xs text-red-600 hover:text-red-800 font-medium transition"
                      >
                        {deletingName === item.name ? '...' : 'Ja'}
                      </button>
                      <span className="text-xs text-gray-400">/</span>
                      <button
                        onClick={() => setConfirmDelete(null)}
                        className="text-xs text-gray-500 hover:text-gray-700 transition"
                      >
                        Nein
                      </button>
                    </span>
                  ) : (
                    <button
                      onClick={() => setConfirmDelete(item.name)}
                      className="text-xs text-red-400 hover:text-red-600 transition"
                    >
                      Löschen
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminMedia;
